import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useWindowSize from '@hooks/useWindowSize';

import { costingTabInfo } from '@features/costing/utils';
import CollectionViewItemsCostingItems from '@features/costing/components/CostingManager/CollectionViewItemsCostingItems';
import RequestViewCostingItems from '@features/costing/components/CostingManager/RequestViewCostingItems';
import StyleViewCostingItems from '@features/costing/components/CostingManager/StyleViewCostingItems';
import {
	allFilterPages,
	costingListDesignStudioFields,
	costingListFilterFields,
	generateCostingListParams,
	initialCostingListFilterFields
} from '@features/filter/static/filterUtil';
import CostingListTopFilter from '@features/filter/v2/core/costing/CostingListTopFilter';
import FilterComponent from '@features/filter/v2/FilterComponent';

import { fetchQuoteCount, setActiveTabInfo } from '@store/actions/CostingAction';
import { handleFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDependentFilterList } from '@services/commonService';

import { checkAnyUserType, checkUserType, isPageReachBottom } from '@utils/Util';

const ListOfCosting = () => {
	const dispatch = useDispatch();
	const [page, setPage] = useState(0);
	const [isShowFilter, setIsShowFilter] = useState(false);
	const listOfCosting = useSelector((store) => store.filterResponseStore);
	const activeTabInfo = useSelector((store) => store.costingStore.activeTabInfo);
	const scrollRef = useRef({});
	const filterfields = useSelector((store) => store.filterFieldStore);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const acceptedRoles = ['COSTING_MANAGER', 'ADMIN', 'MANAGEMENT', 'SUPPLY_CHAIN'];
	const activeTab = activeTabInfo?.activeTabName;
	const isActiveDesignStudioTab = [
		'DESIGN_STUDIO_PENDING',
		'DESIGN_STUDIO_IN_REVIEW',
		'DESIGN_STUDIO_COMPLETED'
	].includes(activeTab);

	useWindowSize(() => setIsShowFilter(false));

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useEffect(() => {
		setCurrentState(listOfCosting);
	}, [listOfCosting]);

	const getSortingValue = () => {
		if (sortValue) {
			const sortingMap = {
				DESIGN_STUDIO_PENDING: {
					'id,desc': 'initialCosting.requestedAt,desc',
					'id,asc': 'initialCosting.requestedAt,asc'
				},
				DESIGN_STUDIO_IN_REVIEW: {
					'id,desc': 'initialCosting.priceLastModifiedAt,desc',
					'id,asc': 'initialCosting.priceLastModifiedAt,asc'
				},
				DESIGN_STUDIO_COMPLETED: {
					'id,asc': 'initialCosting.priceLastModifiedAt,asc',
					'id,desc': 'initialCosting.priceLastModifiedAt,desc'
				}
			};

			return sortingMap[activeTab]?.[sortValue] || sortValue;
		} else {
			const defaultSortingMap = {
				COLLECTION_VIEW_PENDING: 'collectionId,desc',
				COLLECTION_VIEW_COMPLETED: 'collectionId,desc',
				DESIGN_STUDIO_PENDING: 'initialCosting.requestedAt,desc',
				DESIGN_STUDIO_IN_REVIEW: 'initialCosting.priceLastModifiedAt,desc',
				DESIGN_STUDIO_COMPLETED: 'initialCosting.priceLastModifiedAt,desc'
			};

			return defaultSortingMap[activeTab] || 'id,desc';
		}
	};

	useEffect(() => {
		if (activeTabInfo?.filterStatus) {
			window.scrollTo(0, 0);
			dispatch(openLoader());
			const queryParams = generateCostingListParams(
				filterfields,
				activeTabInfo.filterStatus,
				0,
				getSortingValue()
			);
			setPage(0);
			if (isActiveDesignStudioTab) {
				let requestBody = generateCostingListParams(filterfields, activeTabInfo.filterStatus);
				let queryString = `?page=0&size=20&sort=${getSortingValue()}`;
				Promise.all([
					dispatch(
						handleFilter(activeTabInfo.listApiUrl, queryString, false, 'POST', requestBody)
					).finally(() => {
						dispatch(closeLoader());
					})
				]);
			} else {
				Promise.all([
					dispatch(fetchQuoteCount(activeTabInfo.quoteCountApiUrl, queryParams)),
					dispatch(handleFilter(activeTabInfo.listApiUrl, queryParams, false))
				]).finally(() => dispatch(closeLoader()));
			}
		}
	}, [activeTabInfo, filterfields]);

	useEffect(() => {
		if (isShowFilter) {
			const queryParams = generateCostingListParams(
				filterfields,
				activeTabInfo.filterStatus,
				0,
				getSortingValue()
			);
			dispatch(fetchDependentFilterList({ type: activeTabInfo.filterApiFor }, queryParams));
		}
	}, [isShowFilter, activeTabInfo, filterfields]);

	useEffect(() => {
		if (page !== 0 && activeTabInfo) {
			dispatch(openLoader());
			let queryParams = generateCostingListParams(
				filterfields,
				activeTabInfo.filterStatus,
				page,
				getSortingValue()
			);
			if (isActiveDesignStudioTab) {
				let requestBody = generateCostingListParams(filterfields, activeTabInfo.filterStatus);
				let queryString = `?page=${page}&size=20&sort=${getSortingValue()}`;
				dispatch(
					handleFilter(activeTabInfo.listApiUrl, queryString, true, 'POST', requestBody)
				).finally(() => {
					dispatch(closeLoader());
				});
			} else {
				dispatch(handleFilter(activeTabInfo.listApiUrl, queryParams, true)).finally(() => {
					dispatch(closeLoader());
				});
			}
		}
	}, [page]);

	useEffect(() => {
		if (!activeTabInfo) {
			{
				checkAnyUserType(acceptedRoles)
					? dispatch(setActiveTabInfo(costingTabInfo['BUYER_DESIGN_PENDING']))
					: !checkUserType('FASHION_DESIGNER')
						? dispatch(setActiveTabInfo(costingTabInfo['BUYER_DESIGN_PENDING']))
						: dispatch(setActiveTabInfo(costingTabInfo['COLLECTION_VIEW_PENDING']));
			}
		}
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [activeTabInfo]);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};
	const onCollspseFilter = () => setIsShowFilter(!isShowFilter);

	const renderDataTable = () => {
		switch (activeTab) {
			case 'DESIGN_STUDIO_PENDING':
			case 'DESIGN_STUDIO_IN_REVIEW':
			case 'DESIGN_STUDIO_COMPLETED':
				return <StyleViewCostingItems sortValue={getSortingValue()} />;
			case 'COLLECTION_VIEW_PENDING':
			case 'COLLECTION_VIEW_COMPLETED':
				return <CollectionViewItemsCostingItems />;
			default:
				return <RequestViewCostingItems />;
		}
	};

	const getCostingListDesignStudioFields = (costingListDesignStudioFields) => {
		if (activeTab === 'DESIGN_STUDIO_PENDING') {
			const { ...rest } = costingListDesignStudioFields;
			return rest;
		} else {
			const { ...rest } = costingListDesignStudioFields;
			return rest;
		}
	};

	const getFilterFields = () => {
		switch (activeTab) {
			case 'DESIGN_STUDIO_PENDING':
			case 'DESIGN_STUDIO_IN_REVIEW':
			case 'DESIGN_STUDIO_COMPLETED':
				return getCostingListDesignStudioFields(costingListDesignStudioFields);
			case 'COLLECTION_VIEW_PENDING':
			case 'COLLECTION_VIEW_COMPLETED':
				return initialCostingListFilterFields;
			default:
				return costingListFilterFields;
		}
	};

	const renderAlertMessage = () => {
		if (listOfCosting?.data?.length === 0) {
			return (
				<div className="alert alert-secondary text-center w-100">
					<p>No costing request found</p>
				</div>
			);
		}
	};
	return (
		<>
			<CostingListTopFilter toggleFilter={onCollspseFilter} />
			<section className="filter-data-container filter-v3-data-container under-inner-tabs">
				<FilterComponent
					filterPageName={allFilterPages.COSTING_LIST}
					fields={getFilterFields()}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>

				<div className="data-table">
					{renderDataTable()}
					{renderAlertMessage()}
				</div>
			</section>
		</>
	);
};

export default ListOfCosting;
